<template>
    <nav
        v-if="lastPage > 1"
        :class="classes"
        aria-label="Pagination Navigation"
    >
        <ul :class="mdsNS('pagination')">
            <li :class="mdsNS('pagination__item')" v-if="showFirstAndLast">
                <a
                    href="javascript:void(0)"
                    :class="[
                        mdsNS('pagination__link'),
                        getDisabledClass(previousDisabled)
                    ]"
                    :aria-label="firstAriaLabel"
                    role="button"
                    aria-disabled="true"
                    :tabindex="previousDisabled ? -1 : 0"
                    @click.stop="gotoFirstPage"
                    @keyup.enter="gotoFirstPage"
                >
                    <svg
                        v-if="showIcon"
                        :class="mdsNS('icon pagination__icon')"
                        aria-hidden="true"
                    >
                        <use xlink:href="#angle-double-left--s"></use>
                    </svg>
                    {{ showText ? firstLabel : '' }}
                </a>
            </li>
            <li :class="mdsNS('pagination__item')" v-if="showPreviousNext">
                <a
                    href="javascript:void(0)"
                    :class="[
                        mdsNS('pagination__link'),
                        getDisabledClass(previousDisabled)
                    ]"
                    :aria-label="previousAriaLabel"
                    role="button"
                    aria-disabled="true"
                    :tabindex="previousDisabled ? -1 : 0"
                    @click.stop="gotoPreviousPage"
                    @keyup.enter="gotoPreviousPage"
                >
                    <svg
                        v-if="showIcon"
                        :class="mdsNS('icon pagination__icon')"
                        aria-hidden="true"
                    >
                        <use xlink:href="#angle-left--s"></use>
                    </svg>
                    {{ showText ? previousLabel : '' }}
                </a>
            </li>
            <li
                :class="mdsNS('pagination__item')"
                v-for="page in pages"
                :key="page.id"
            >
                <a
                    href="javascript:void(0)"
                    :class="page.classes"
                    role="button"
                    :aria-label="page.ariaLabel"
                    :aria-current="page.ariaCurrent"
                    @click.stop="gotoPage(page)"
                    @keyup.enter="gotoPage(page)"
                >
                    {{ page.id }}
                </a>
            </li>
            <li :class="mdsNS('pagination__item')" v-if="showPreviousNext">
                <a
                    href="javascript:void(0)"
                    :class="[
                        mdsNS('pagination__link'),
                        getDisabledClass(nextDisabled)
                    ]"
                    role="button"
                    :aria-label="nextAriaLabel"
                    @click.stop="gotoNextPage"
                    @keyup.enter="gotoNextPage"
                    :tabindex="nextDisabled ? -1 : 0"
                >
                    <svg
                        v-if="showIcon"
                        :class="mdsNS('icon pagination__icon')"
                        aria-hidden="true"
                    >
                        <use xlink:href="#angle-right--s"></use>
                    </svg>
                    {{ showText ? nextLabel : '' }}
                </a>
            </li>
            <li :class="mdsNS('pagination__item')" v-if="showFirstAndLast">
                <a
                    href="javascript:void(0)"
                    :class="[
                        mdsNS('pagination__link'),
                        getDisabledClass(nextDisabled)
                    ]"
                    role="button"
                    :aria-label="lastAriaLabel"
                    @click.stop="gotoLastPage"
                    @keyup.enter="gotoLastPage"
                    :tabindex="nextDisabled ? -1 : 0"
                >
                    <svg
                        v-if="showIcon"
                        :class="mdsNS('icon pagination__icon')"
                        aria-hidden="true"
                    >
                        <use xlink:href="#angle-double-right--s"></use>
                    </svg>
                    {{ showText ? lastLabel : '' }}
                </a>
            </li>
        </ul>
    </nav>
</template>
<script>
import utils from '../../utils';
import mixins from '../mixins';
export default {
    name: 'markets-ui-pagination',
    mixins: [mixins],
    props: {
        total: {
            type: Number,
            required: true
        },
        page: {
            type: Number,
            default: 1
        },
        perPage: {
            type: Number,
            required: true
        },
        numberOfWrappingPageLinks: {
            type: Number,
            default: 2
        },
        showFirstAndLast: {
            type: Boolean,
            default: true
        },
        showPreviousNext: {
            type: Boolean,
            default: true
        },
        showIcon: {
            type: Boolean,
            default: true
        },
        showPageNumbers: {
            type: Boolean,
            default: true
        },
        firstLabel: {
            type: String,
            default: 'First'
        },
        firstAriaLabel: {
            type: String,
            default: 'Go to first page'
        },
        previousLabel: {
            type: String,
            default: 'Previous'
        },
        previousAriaLabel: {
            type: String,
            default: 'Go to previous page'
        },
        nextLabel: {
            type: String,
            default: 'Next'
        },
        nextAriaLabel: {
            type: String,
            default: 'Go to next page'
        },
        lastLabel: {
            type: String,
            default: 'Last'
        },
        lastAriaLabel: {
            type: String,
            default: 'Go to last page'
        }
    },
    data() {
        return {
            currentPage: 1
        };
    },
    watch: {
        page: {
            handler(val) {
                if (val >= 1) {
                    this.currentPage = val;
                }
            },
            immediate: true
        },
        lastPage: {
            handler(val) {
                // goto last page after delete all recorder in last page.
                if (this.currentPage > val) {
                    this.gotoLastPage();
                }
            }
        }
    },
    computed: {
        classes() {
            const cls = [
                this.mdsNS('pagination__wrapper'),
                this.namespace(),
                this.skinStyle
            ];
            if (this.skin) {
                cls.push(
                    `${this.namespace()}__${utils.getSkinSuffix(this.skin)}`
                );
            }
            return cls;
        },
        showText() {
            return !this.showIcon;
        },
        lastPage() {
            if (this.perPage > 0) {
                return Math.ceil(this.total / this.perPage);
            } else {
                return this.perPage;
            }
        },
        pages() {
            if (this.showPageNumbers) {
                const beforeRemaining = Math.min(
                    0,
                    this.currentPage - this.numberOfWrappingPageLinks - 1
                );
                const afterRemaining = Math.min(
                    0,
                    this.lastPage -
                        (this.currentPage + this.numberOfWrappingPageLinks)
                );
                const start = Math.max(
                    1,
                    this.currentPage -
                        this.numberOfWrappingPageLinks +
                        afterRemaining
                );
                const end = Math.min(
                    this.lastPage,
                    this.currentPage +
                        this.numberOfWrappingPageLinks -
                        beforeRemaining
                );
                const pages = [];
                for (let i = start; i <= end; i++) {
                    const selected = i === this.currentPage;
                    const classes = [
                        this.mdsNS('pagination__link'),
                        this.namespace('__link')
                    ];
                    if (selected) {
                        classes.push(
                            this.mdsNS('pagination__link--selected'),
                            this.namespace('__link--selected')
                        );
                    }
                    pages.push({
                        id: i,
                        selected,
                        ariaLabel: selected
                            ? `Current Page, Page ${i}`
                            : `Go to Page ${i}`,
                        ariaCurrent: selected ? 'page' : '',
                        classes
                    });
                }
                return pages;
            } else {
                return [];
            }
        },
        nextDisabled() {
            return this.currentPage === this.lastPage;
        },
        previousDisabled() {
            return this.currentPage === 1;
        }
    },
    created() {
        this.namespace = utils.namespace('ui-pagination', false);
    },
    methods: {
        getDisabledClass(isDisabled) {
            if (isDisabled) {
                return this.mdsNS('pagination__link--disabled');
            }
            return '';
        },
        gotoPage(page) {
            this.changePage(page.id);
        },
        gotoFirstPage() {
            if (this.previousDisabled) {
                return;
            }
            this.changePage(1);
        },
        gotoLastPage() {
            if (this.nextDisabled) {
                return;
            }
            this.changePage(this.lastPage);
        },
        gotoPreviousPage() {
            if (this.previousDisabled) {
                return;
            }
            this.changePage(this.currentPage - 1);
        },
        gotoNextPage() {
            if (this.nextDisabled) {
                return;
            }
            this.changePage(this.currentPage + 1);
        },
        changePage(page) {
            if (this.currentPage !== page) {
                this.currentPage = page;
                this.$emit('change', {
                    page,
                    startAt: (page - 1) * this.perPage + 1,
                    endAt: Math.min(page * this.perPage, this.total)
                });
            }
        }
    }
};
</script>
<style lang="scss">
@import '../../mds/constants';
$namespace: 'markets-ui-pagination';
.#{$namespace} {
    &__dark-gray {
        .#{$namespace}__link {
            color: $mds-text-color-secondary-on-dark;
        }
    }
}
</style>
